import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Checkbox,
  IconButton,
  InputAdornment,
  Stack,
  Chip,
} from '@mui/material';
import {
  Search as SearchIcon,
  Close as CloseIcon,
  Add as AddIcon,
  DeleteOutline as DeleteOutlineIcon,
} from '@mui/icons-material';
import { useDispatch } from 'react-redux';
import { showSnackbar } from '../../../../Reducer/SnackbarReducer';
import { handleQuerySuccess } from '../../../../hooks/useQuriesSuccess';
import {
  useSearchWorkTypeCodes,
  useSearchWorkTypeCustMutation,
  useSaveWorkTypeCust,
  useDeleteWorkTypeCust,
} from './SAM_BASIC_02_WorkType_QUERY';
import {
  MapStandardWorkTypeRows,
  MapCustWorkTypeRows,
  MergeWorkTypeLists,
} from './SAM_BASIC_02_WorkTypeUtils';

function NormalizeSelectedCodes(selectedWorkTypes) {
  if (!Array.isArray(selectedWorkTypes)) return [];
  return selectedWorkTypes
    .map((x) => {
      if (x != null && typeof x === 'object' && x.code != null) {
        return String(x.code);
      }
      return x != null ? String(x) : '';
    })
    .filter(Boolean);
}

/**
 * 작업 종류 선택 팝업
 * @param {Boolean} open - 팝업 열림 여부
 * @param {Function} onClose - 닫기 콜백
 * @param {Function} onSelect - 선택 콜백 (선택된 작업 종류 배열 전달)
 * @param {Array} selectedWorkTypes - 이미 선택된 작업 종류 배열 ({ code, codeNm } 또는 코드)
 * @param {String} custCd - 사업장코드 (커스텀 작업종류 조회·등록)
 * @param {Object} userInfo - 사용자 정보 (저장 시 UserId)
 */
function WorkTypeSelectPopup({
  open,
  onClose,
  onSelect,
  selectedWorkTypes = [],
  custCd = '',
  userInfo = {},
}) {
  const dispatch = useDispatch();

  const [workTypeList, setWorkTypeList] = useState([]);
  const [filteredList, setFilteredList] = useState([]);
  const [selectedItems, setSelectedItems] = useState([]);
  const [searchText, setSearchText] = useState('');
  const [newWorkTypeNm, setNewWorkTypeNm] = useState('');
  const [adding, setAdding] = useState(false);

  const custContext = { search: { custCd: custCd || '' }, userInfo };

  const { runAsync: SearchWorkTypeCodes } = useSearchWorkTypeCodes();
  const { runAsync: SearchWorkTypeCust } = useSearchWorkTypeCustMutation();
  const { runAsync: SaveWorkTypeCustAsync } = useSaveWorkTypeCust({
    search: { custCd: custCd || '' },
    userInfo,
  });
  const { runAsync: DeleteWorkTypeCustAsync } = useDeleteWorkTypeCust({
    userInfo,
  });

  const applyMergedList = useCallback((standard, cust) => {
    const merged = MergeWorkTypeLists(standard, cust);
    setWorkTypeList(merged);
    setFilteredList(merged);
  }, []);

  const SearchWorkType = useCallback(async () => {
    if (!custCd?.trim()) {
      dispatch(
        showSnackbar({
          message: '사업장을 선택한 후 작업종류를 조회해 주세요.',
          type: 'warning',
        })
      );
      setWorkTypeList([]);
      setFilteredList([]);
      return;
    }

    try {
      let standard = [];
      let cust = [];

      await SearchWorkTypeCodes(null, {
        onSuccess: (rows) => {
          standard = MapStandardWorkTypeRows(rows);
        },
      });

      await SearchWorkTypeCust(custContext, {
        onSuccess: (rows) => {
          cust = MapCustWorkTypeRows(rows);
        },
      });

      applyMergedList(standard, cust);
    } catch (error) {
      dispatch(
        showSnackbar({
          message: error?.message || '조회 중 오류가 발생했습니다.',
          type: 'error',
        })
      );
      setWorkTypeList([]);
      setFilteredList([]);
    }
  }, [
    SearchWorkTypeCodes,
    SearchWorkTypeCust,
    custContext,
    custCd,
    applyMergedList,
    dispatch,
  ]);

  // 검색 필터링
  useEffect(() => {
    if (!searchText.trim()) {
      setFilteredList(workTypeList);
    } else {
      const filtered = workTypeList.filter((item) =>
        item.codeNm.toLowerCase().includes(searchText.toLowerCase())
      );
      setFilteredList(filtered);
    }
  }, [searchText, workTypeList]);

  // 팝업 열릴 때 초기화
  useEffect(() => {
    if (open) {
      SearchWorkType();
      setSelectedItems(NormalizeSelectedCodes(selectedWorkTypes));
      setSearchText('');
      setNewWorkTypeNm('');
    }
  }, [open, custCd]);

  const handleAddCustWorkType = async () => {
    const name = newWorkTypeNm.trim();
    if (!custCd?.trim()) {
      dispatch(
        showSnackbar({
          message: '사업장을 선택해 주세요.',
          type: 'warning',
        })
      );
      return;
    }
    if (!name) {
      dispatch(
        showSnackbar({
          message: '작업종류명을 입력해 주세요.',
          type: 'warning',
        })
      );
      return;
    }

    const dup = workTypeList.some(
      (x) => String(x.codeNm ?? '').trim() === name
    );
    if (dup) {
      dispatch(
        showSnackbar({
          message: '이미 등록된 작업종류명입니다.',
          type: 'warning',
        })
      );
      return;
    }

    setAdding(true);
    try {
      const response = await SaveWorkTypeCustAsync({
        custCd,
        workTypeNm: name,
        isPrimary: 'N',
      });

      let created = null;
      await handleQuerySuccess(response, {
        onSuccess: (rows) => {
          const mapped = MapCustWorkTypeRows(rows);
          created = mapped[0] || null;
        },
      });

      if (!created?.code) {
        throw new Error('작업종류 등록에 실패했습니다.');
      }

      dispatch(
        showSnackbar({
          message: '작업종류가 추가되었습니다.',
          type: 'success',
        })
      );
      setNewWorkTypeNm('');
      await SearchWorkType();
      setSelectedItems((prev) =>
        prev.includes(created.code) ? prev : [...prev, created.code]
      );
    } catch (error) {
      dispatch(
        showSnackbar({
          message: error?.message || '작업종류 추가 중 오류가 발생했습니다.',
          type: 'error',
        })
      );
    } finally {
      setAdding(false);
    }
  };

  const handleDeleteCustWorkType = async (item, e) => {
    e?.stopPropagation?.();
    if (!item?.isCust || !custCd?.trim()) return;

    try {
      await DeleteWorkTypeCustAsync({
        custCd,
        workType: item.code,
      });
      dispatch(
        showSnackbar({
          message: `"${item.codeNm}" 작업종류가 목록에서 제거되었습니다.`,
          type: 'success',
        })
      );
      setSelectedItems((prev) =>
        prev.filter((c) => String(c) !== String(item.code))
      );
      await SearchWorkType();
    } catch (error) {
      dispatch(
        showSnackbar({
          message:
            error?.message ||
            `"${item.codeNm}" 작업종류를 목록에서 제거할 수 없습니다. 항목 관리에 등록된 경우 먼저 삭제해야 합니다.`,
          type: 'warning',
        })
      );
    }
  };

  const handleToggle = (item) => {
    const code = String(item.code ?? '');
    setSelectedItems((prev) => {
      if (prev.some((c) => String(c) === code)) {
        return prev.filter((c) => String(c) !== code);
      }
      return [...prev, code];
    });
  };

  const handleSelect = () => {
    if (selectedItems.length === 0) {
      dispatch(
        showSnackbar({
          message: '항목 관리에 추가할 작업종류를 선택해 주세요.',
          type: 'warning',
        })
      );
      return;
    }
    const selectedRows = workTypeList.filter((row) =>
      selectedItems.some((c) => String(row.code) === String(c))
    );
    onSelect?.(selectedRows);
    onClose();
  };

  const selectedCount = selectedItems.length;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 0,
          overflow: 'hidden',
          maxHeight: '90vh',
          display: 'flex',
          flexDirection: 'column',
        },
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          maxHeight: '90vh',
          overflow: 'hidden',
          bgcolor: '#f6f7f8',
        }}
      >
        {/* Top Navigation Bar */}
        <Paper
          elevation={0}
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            alignItems: 'center',
            borderBottom: '1px solid #f0f2f4',
            bgcolor: 'white',
            px: 3,
            py: 1.5,
            flexShrink: 0,
            zIndex: 20,
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 2,
              color: '#111418',
            }}
          >
            <Box
              sx={{
                width: 32,
                height: 32,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: 'rgba(19, 127, 236, 0.1)',
                color: 'primary.main',
                borderRadius: 1.5,
              }}
            >
              <Typography
                sx={{
                  fontSize: '20px',
                }}
              >
                📋
              </Typography>
            </Box>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 0.25,
                minWidth: 0,
              }}
            >
              <Typography
                variant="h6"
                sx={{
                  color: '#111418',
                  fontSize: 22,
                  fontWeight: 'bold',
                  letterSpacing: -0.15,
                }}
              >
                작업 종류 선택
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  display: 'block',
                  color: '#617589',
                  fontSize: '0.75rem',
                  lineHeight: 1.4,
                  whiteSpace: 'normal',
                }}
              >
                표준·사업장 작업종류를 체크한 뒤 하단 「확인」을 누르면 항목 관리 목록에 추가됩니다.
              </Typography>
            </Box>
          </Box>
          <IconButton
            onClick={onClose}
            size="small"
            sx={{
              color: '#617589',
              '&:hover': {
                color: '#111418',
                bgcolor: 'transparent',
              },
            }}
          >
            <CloseIcon />
          </IconButton>
        </Paper>

        {/* 컨텐츠 */}
        <Box
          sx={{
            flex: 1,
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
            minHeight: 0,
            p: 2,
          }}
        >
          <TextField
            fullWidth
            size="small"
            placeholder="작업 종류 검색"
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: '#617589' }} />
                </InputAdornment>
              ),
            }}
            sx={{
              mb: 2,
              '& .MuiOutlinedInput-root': {
                backgroundColor: 'white',
                '& fieldset': {
                  borderColor: '#e5e7eb',
                },
                '&:hover fieldset': {
                  borderColor: '#d1d5db',
                },
                '&.Mui-focused fieldset': {
                  borderColor: 'primary.main',
                },
              },
            }}
          />

          <Paper
            elevation={0}
            sx={{
              p: 1.5,
              mb: 2,
              border: '1px solid #e5e7eb',
              borderRadius: 1,
              bgcolor: 'white',
            }}
          >
            <Typography
              variant="caption"
              sx={{ color: '#617589', display: 'block', mb: 1 }}
            >
              사업장 전용 작업종류 추가
            </Typography>
            <Stack direction="row" spacing={1} alignItems="flex-start">
              <TextField
                size="small"
                fullWidth
                placeholder="작업종류명 입력"
                value={newWorkTypeNm}
                onChange={(e) => setNewWorkTypeNm(e.target.value)}
                disabled={adding || !custCd}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleAddCustWorkType();
                  }
                }}
              />
              <Button
                variant="outlined"
                size="small"
                startIcon={<AddIcon />}
                disabled={adding || !custCd}
                onClick={handleAddCustWorkType}
                sx={{ whiteSpace: 'nowrap', minWidth: 88 }}
              >
                추가
              </Button>
            </Stack>
          </Paper>

          <Paper
            elevation={0}
            sx={{
              flex: 1,
              overflowY: 'auto',
              overflowX: 'hidden',
              minHeight: 120,
              border: '1px solid #f0f2f4',
              borderRadius: 1,
              backgroundColor: 'white',
              '&::-webkit-scrollbar': {
                width: '8px',
              },
              '&::-webkit-scrollbar-thumb': {
                backgroundColor: 'rgba(0, 0, 0, 0.2)',
                borderRadius: '4px',
              },
              '&::-webkit-scrollbar-track': {
                backgroundColor: '#f1f1f1',
              },
            }}
          >
            <List>
              {filteredList.length === 0 ? (
                <ListItem>
                  <ListItemText
                    primary="조회된 작업 종류가 없습니다."
                    sx={{
                      textAlign: 'center',
                      color: '#617589',
                      py: 4,
                    }}
                  />
                </ListItem>
              ) : (
                filteredList.map((item, index) => {
                  const codeStr = String(item.code ?? '');
                  const isChecked = selectedItems.some(
                    (c) => String(c) === codeStr
                  );
                  return (
                  <ListItem
                    key={codeStr || index}
                    disablePadding
                    sx={{
                      backgroundColor: isChecked
                        ? 'rgba(19, 127, 236, 0.08)'
                        : 'transparent',
                      borderBottom: '1px solid #f0f2f4',
                      '&:hover': {
                        backgroundColor: isChecked
                          ? 'rgba(19, 127, 236, 0.12)'
                          : '#f9fafb',
                      },
                    }}
                  >
                    <ListItemButton
                      onClick={() => handleToggle(item)}
                      dense
                      sx={{
                        py: 1.5,
                        px: 2,
                      }}
                    >
                      <Checkbox
                        edge="start"
                        checked={isChecked}
                        tabIndex={-1}
                        disableRipple
                      />
                      <ListItemText
                        primary={
                          <Stack
                            direction="row"
                            alignItems="center"
                            spacing={1}
                            flexWrap="wrap"
                          >
                            <span>{item.codeNm}</span>
                            {item.isCust ? (
                              <Chip
                                label="사업장"
                                size="small"
                                sx={{ height: 20, fontSize: '0.65rem' }}
                              />
                            ) : null}
                          </Stack>
                        }
                        sx={{
                          '& .MuiListItemText-primary': {
                            fontWeight: isChecked ? 600 : 400,
                            color: '#111418',
                            fontSize: '0.875rem',
                          },
                        }}
                      />
                      {item.isCust ? (
                        <IconButton
                          size="small"
                          edge="end"
                          aria-label="사업장 작업종류 삭제"
                          onClick={(e) => handleDeleteCustWorkType(item, e)}
                          sx={{ color: '#9ca3af' }}
                        >
                          <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                      ) : null}
                    </ListItemButton>
                  </ListItem>
                  );
                })
              )}
            </List>
          </Paper>
        </Box>

        {/* 하단 버튼 — 스크롤 영역 밖 고정 */}
        <Paper
          elevation={0}
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 1.5,
            px: 3,
            py: 2,
            borderTop: '1px solid #f0f2f4',
            bgcolor: 'white',
            flexShrink: 0,
          }}
        >
          <Typography variant="body2" color="text.secondary">
            {selectedCount > 0
              ? `${selectedCount}건 선택됨`
              : '작업종류를 선택해 주세요'}
          </Typography>
          <Box sx={{ display: 'flex', gap: 1.5 }}>
            <Button
              onClick={onClose}
              variant="outlined"
              sx={{
                minWidth: '100px',
                color: '#617589',
                borderColor: '#e5e7eb',
                fontWeight: 500,
                '&:hover': {
                  borderColor: '#d1d5db',
                  bgcolor: 'transparent',
                },
              }}
            >
              취소
            </Button>
            <Button
              onClick={handleSelect}
              variant="contained"
              disabled={selectedCount === 0}
              sx={{
                minWidth: '100px',
                fontWeight: 500,
              }}
            >
              확인
            </Button>
          </Box>
        </Paper>
      </Box>
    </Dialog>
  );
}

export default WorkTypeSelectPopup;
